import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { LeadSplitView, type LeadSplitApi } from '../../components/speedToLead/LeadSplitView';
import { PageHeader } from '../../components/layout/PageHeader';
import { KC } from '../../lib/kyckConnect/links';
import { fetchLeadClubs, fetchLeadSplit, fetchLeadSplitDetail, fetchRecordingUrl, setLeadNeedsPerson, setLeadOutcome, fetchLeadCallOptions, startLeadCall, sendLeadText, setLeadSamPaused } from './api';

// THE KYCK LEADS HOSTS (Phase 2, Part B): the shared LeadSplitView over the STAFF api, in the CURRENT staff look (no theme wrapper).
// ⚠️ The club's portal uses its own api file and never imports this one (a source guard pins it).

export function kyckSplitApi(): LeadSplitApi {
  return {
    list: (filters, cursor, accountId) => fetchLeadSplit(accountId, filters, cursor),
    detail: (id, cursor) => fetchLeadSplitDetail(id, cursor),
    setOutcome: (id, code) => setLeadOutcome(id, code),
    setNeedsPerson: (id, on) => setLeadNeedsPerson(id, on),
    callOptions: (id) => fetchLeadCallOptions(id),
    startCall: (id, teamMemberId) => startLeadCall(id, teamMemberId).then(() => undefined),
    sendText: (id, text) => sendLeadText(id, text),
    setSamPaused: (id, paused) => setLeadSamPaused(id, paused),
    loadPlayUrl: async (kind, id) => (await fetchRecordingUrl(kind, id)).url,
  };
}

const API = kyckSplitApi();

/** One club's leads: the account page's Leads tab. */
export function LeadsSection({ accountId }: { accountId: string }) {
  return <LeadSplitView api={API} queryKey={['speed-to-lead', 'leads', accountId]} accountId={accountId} showTestToggle approvalsHref={KC.approvals} />;
}

/** Every club's leads (admin only): the KyckConnect Leads page, with a club filter. */
export function AllLeadsPage() {
  // `?needsPerson=true` (the Overview's tile) opens the "Needs your team" tab.
  const [params] = useSearchParams();
  const initialStage = params.get('needsPerson') === 'true' ? 'needs_team' : 'all';
  // The clubs with their brand and group, resolved SERVER-side from the canonical resolvers (admin only, like this page).
  const clubs = useQuery({ queryKey: ['speed-to-lead', 'lead-clubs'], queryFn: fetchLeadClubs, staleTime: 5 * 60_000 });
  return (
    <div>
      <PageHeader title="Leads" description="Every lead across all clubs, newest first. Narrow it by brand, group or club, or open a club's account page and its KyckConnect tab." />
      <LeadSplitView api={API} queryKey={['speed-to-lead', 'leads', 'all']} staffClubs={clubs.data} showTestToggle approvalsHref={KC.approvals} initialStage={initialStage} />
    </div>
  );
}
